<template>
  <v-expansion-panel>
    <v-expansion-panel-header v-slot="{ open }">
      <v-row no-gutters>
        <v-col cols="4" class="blue--text text--darken-1"> Fiche de Préparation </v-col>
        <v-col cols="8" class="text--secondary">
          <v-fade-transition leave-absolute>
            <span v-if="open" key="0"> Editer la fiche puis sauvegarder </span>
            <span v-else key="1">
              <div v-if="fichePreparation.account">
                {{ fichePreparation.account.name }}
              </div>
              <div v-if="fichePreparation.laverie">
                / {{ fichePreparation.laverie.name }}
              </div>
            </span>
          </v-fade-transition>
        </v-col>
      </v-row>
    </v-expansion-panel-header>
    <v-expansion-panel-content>
      <v-row no-gutters>
        <v-spacer></v-spacer>
        <v-col cols="6">
          <v-select
            v-model="fichePreparation.account"
            :items="accounts"
            :item-text="'name'"
            return-object
            label="Siège"
            @update:model-value="onAccountChange"
          ></v-select>
        </v-col>
        <v-divider vertical class="mx-4"></v-divider>
        <v-col cols="3">
          <br />
          Choisissez le compte siège
        </v-col>
      </v-row>
      <v-row v-if="fichePreparation.account" no-gutters>
        <v-spacer></v-spacer>
        <v-col cols="6">
          <v-select
            v-model="fichePreparation.laverie"
            :items="laveries"
            :item-text="'name'"
            return-object
            label="Laverie"
            @update:model-value="onChange"
          ></v-select>
        </v-col>
        <v-divider vertical class="mx-4"></v-divider>
        <v-col cols="3">
          <br />
          Choisissez la laverie
        </v-col>
      </v-row>
      <v-row v-if="fichePreparation.laverie" no-gutters>
        <v-spacer></v-spacer>
        <v-col cols="6">
          <template>
            <v-text-field
              v-bind="props"
              label="Préparer pour"
              :model-value="preparedForFormatted"
              @update:model-value="onPreparedForUpdate"
              prepend-icon="mdi-calendar"
              type="date"
              class="input-group--focused ml-5"
              @blur="onBlur"
            >
              <template #label>
                Date de Démarrage<span class="text-red"><strong> *</strong></span>
              </template>
            </v-text-field>
          </template>
        </v-col>
        <v-divider vertical class="mx-4"></v-divider>
        <v-col cols="3">
          <br />
          Choisissez la date
        </v-col>
      </v-row>
      <v-row v-if="fichePreparation.preparedFor" no-gutters>
        <v-spacer></v-spacer>
        <v-col cols="6">
          <v-select
            v-model="fichePreparation.preparedBy"
            :items="users"
            :item-text="(item) => `${item.name} ${item.lastname}`"
            return-object
            label="Préparé par"
            @update:model-value="onChange"
          ></v-select>
        </v-col>
        <v-divider vertical class="mx-4"></v-divider>
        <v-col cols="3">
          <br />
          Choisissez le préparateur
        </v-col>
      </v-row>
      <v-row
        v-if="
          fichePreparation.preparedBy &&
          fichePreparation.preparedBy.lastname == 'personne'
        "
        no-gutters
      >
        <v-spacer></v-spacer>
        <v-col cols="6">
          <v-text-field
            name="autreUserLastname"
            v-model="fichePreparation.preparedBy.name"
            class="input-group--focused"
            label="Précisez"
            @blur="onBlur"
          >
          </v-text-field>
        </v-col>
        <v-divider vertical class="mx-4"></v-divider>
        <v-col cols="3"> </v-col>
      </v-row>
      <v-row
        v-if="fichePreparation.preparedBy && fichePreparation.preparedBy.lastname"
        no-gutters
      >
        <v-spacer></v-spacer>
        <v-col cols="6">
          <v-select
            v-model="fichePreparation.deliveredBy"
            :items="users"
            :item-text="(item) => `${item.name} ${item.lastname}`"
            return-object
            label="Livraison par"
            @update:model-value="onChange"
          ></v-select>
        </v-col>
        <v-divider vertical class="mx-4"></v-divider>
        <v-col cols="3">
          <br />
          Choisissez le livreur
        </v-col>
      </v-row>
      <v-row
        v-if="
          fichePreparation.deliveredBy &&
          fichePreparation.deliveredBy.lastname == 'personne'
        "
        no-gutters
      >
        <v-spacer></v-spacer>
        <v-col cols="6">
          <v-text-field
            name="autreUserLastname1"
            v-model="fichePreparation.deliveredBy.name"
            class="input-group--focused"
            label="Précisez"
            @blur="onBlur"
          >
          </v-text-field>
        </v-col>
        <v-divider vertical class="mx-4"></v-divider>
        <v-col cols="3"> </v-col>
      </v-row>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn @click="onCancel" text color="red"> Cancel </v-btn>
        <generate-doc-btn
          :filePath="fichePreparationTemplatePath"
          :fileName="fichePreparationFileName"
          :dataSet="fichePreparationGen"
          >Save</generate-doc-btn
        >
      </v-card-actions>
    </v-expansion-panel-content>
  </v-expansion-panel>
</template>

<script setup lang="ts">
import { ref, computed } from "vue";
import GenerateDocBtn from "@/components/GenerateDocBtn.vue";
import type { FichePreparation, FichePreparationGen } from "@/types";
import { flattenFichePreparation } from "@/types";
import type { Account } from "@/types";
import type { Laverie } from "@/types";
import type { User } from "@/types";
import { getLaveriesByAccountId } from "@/utils/laverie-api";

// Props
interface Props {
  accounts: Account[];
  users: User[];
}

const props = defineProps<Props>();

// Reactive state
const dateMenu = ref(false);
const fichePreparation = ref<FichePreparation>({} as FichePreparation);
const fichePreparationGen = ref<FichePreparationGen>({} as FichePreparationGen);
const laveries = ref<Laverie[]>([]);

// Computed properties
const fichePreparationTemplatePath = computed(() => {
  return "templates/FichePreparationTemplate.docx";
});

const fichePreparationFileName = computed(() => {
  if (fichePreparation.value.laverie?.name) {
    return `FicheDePreparation_${fichePreparation.value.laverie.name}`;
  }
  return "FicheDePreparation";
});

const preparedForFormatted = computed({
  get: () => {
    if (!fichePreparation.value.preparedFor) return "";
    return fichePreparation.value.preparedFor.toLocaleDateString();
  },
  set: (value: string) => {
    if (value) {
      fichePreparation.value.preparedFor = new Date(value);
    } else {
      fichePreparation.value.preparedFor = null;
    }
  },
});

const onPreparedForUpdate = (value: string) => {
  if (value) {
    fichePreparation.value.preparedFor = new Date(value);
  }
};

// Methods
const onAccountChange = async (selectedAccount: Account) => {
  fichePreparation.value.account = selectedAccount;
  // Reset selected laverie
  fichePreparation.value.laverie = null;
  // Clear previous data
  laveries.value = [];
  await fetchLaveries(selectedAccount.id);
  // Regenerate document data
  fichePreparationGen.value = flattenFichePreparation(fichePreparation.value);
};

const fetchLaveries = async (accountId: string) => {
  try {
    laveries.value = await getLaveriesByAccountId(accountId);
  } catch (error) {
    console.error("Error fetching laveries:", error);
    laveries.value = [];
  }
};

const onChange = () => {
  onBlur();
};

const onBlur = () => {
  fichePreparationGen.value = flattenFichePreparation(fichePreparation.value);
};

const onCancel = () => {
  fichePreparation.value = {} as FichePreparation;
  fichePreparationGen.value = {} as FichePreparationGen;
  laveries.value = [];
  dateMenu.value = false;
};
</script>
